import { apiClient } from './client';
import type { DatasetQueryRequest, DatasetExportRequest } from '@/features/datasets/types/query.types';
import type { SavedQueryCreate } from '@/features/datasets/types/savedQuery.types';

/**
 * Authentication endpoints
 */
export const authEndpoints = {
  login: (credentials: { email: string; password: string }) =>
    apiClient.post('/auth/login', credentials, { withCredentials: true }),

  logout: () => apiClient.post('/auth/logout', {}, { withCredentials: true }),

  refresh: () => apiClient.post('/auth/refresh', {}, { withCredentials: true }),

  me: () => apiClient.get('/auth/me'),

  validateSetupToken: (token: string) =>
    apiClient.get('/auth/set-password/validate', { params: { token } }),

  setPassword: (data: { token: string; new_password: string }) =>
    apiClient.post('/auth/set-password', data),

  forgotPassword: (email: string) =>
    apiClient.post('/auth/forgot-password', { email }),

  validateResetToken: (token: string) =>
    apiClient.get('/auth/reset-password/validate', { params: { token } }),

  resetPassword: (data: { token: string; new_password: string }) =>
    apiClient.post('/auth/reset-password', data),

  ssoConfig: () => apiClient.get('/auth/sso/config'),
};

/**
 * Data source endpoints
 */
export const sourceEndpoints = {
  list: (params?: { page?: number; limit?: number }) =>
    apiClient.get('/sources', { params }),

  get: (id: string) => apiClient.get(`/sources/${id}`),

  create: (data: any) => apiClient.post('/sources', data),

  update: (id: string, data: any) => apiClient.patch(`/sources/${id}`, data),

  delete: (id: string) => apiClient.delete(`/sources/${id}`),

  test: (id: string) => apiClient.post(`/sources/${id}/test`),
};

/**
 * Dataset endpoints
 */
export const datasetEndpoints = {
  list: (params?: {
    page?: number;
    size?: number;
    source_id?: string;
    search?: string;
    sort?: string;
    order?: 'asc' | 'desc';
  }) => apiClient.get('/datasets', { params }),

  get: (id: string, params?: { include_stats?: boolean }) =>
    apiClient.get(`/datasets/${id}`, { params }),

  create: (data: any) => apiClient.post('/datasets', data),

  update: (id: string, data: any) => apiClient.patch(`/datasets/${id}`, data),

  delete: (id: string, params?: { force?: boolean; hard_delete?: boolean }) =>
    apiClient.delete(`/datasets/${id}`, { params }),

  preview: (id: string, params?: { limit?: number }) =>
    apiClient.get(`/datasets/${id}/preview`, { params }),

  schema: (id: string) => apiClient.get(`/datasets/${id}/schema`),

  query: (id: string, data: DatasetQueryRequest) =>
    apiClient.post(`/datasets/${id}/query`, data),

  export: (id: string, data: DatasetExportRequest) =>
    apiClient.post(`/datasets/${id}/export`, data, { responseType: 'blob' }),
};

/**
 * Ingestion workflow endpoints
 */
export const ingestionEndpoints = {
  // Upload file to start ingestion
  upload: (formData: FormData, onProgress?: (progress: number) => void) =>
    apiClient.post('/ingestion/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: (progressEvent) => {
        if (onProgress && progressEvent.total) {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          onProgress(percentCompleted);
        }
      },
    }),

  // Get ingestion job status
  status: (jobId: string) => apiClient.get(`/ingestion/jobs/${jobId}`),

  // List ingestion jobs
  listJobs: (params?: { page?: number; limit?: number; status?: string }) =>
    apiClient.get('/ingestion/jobs', { params }),

  // Validate uploaded data
  validate: (jobId: string, config?: any) =>
    apiClient.post(`/ingestion/jobs/${jobId}/validate`, config),

  // Extract and validate schema
  schema: (jobId: string, config?: any) =>
    apiClient.post(`/ingestion/jobs/${jobId}/schema`, config),

  // Register in catalog
  catalog: (jobId: string, metadata: any) =>
    apiClient.post(`/ingestion/jobs/${jobId}/catalog`, metadata),

  // Run quality checks
  quality: (jobId: string, rules?: any) =>
    apiClient.post(`/ingestion/jobs/${jobId}/quality`, rules),

  // Finalize ingestion
  finalize: (jobId: string) =>
    apiClient.post(`/ingestion/jobs/${jobId}/finalize`),

  // Cancel ingestion job
  cancel: (jobId: string) =>
    apiClient.post(`/ingestion/jobs/${jobId}/cancel`),
};

/**
 * Catalog endpoints
 */
export const catalogEndpoints = {
  search: (params: { query?: string; filters?: any; page?: number; limit?: number }) =>
    apiClient.get('/catalog/search', { params }),

  metadata: (assetId: string) =>
    apiClient.get(`/catalog/assets/${assetId}/metadata`),

  lineage: (assetId: string) =>
    apiClient.get(`/catalog/assets/${assetId}/lineage`),

  tags: () => apiClient.get('/catalog/tags'),

  updateTags: (assetId: string, tags: string[]) =>
    apiClient.put(`/catalog/assets/${assetId}/tags`, { tags }),
};

/**
 * Quality check endpoints
 */
export const qualityEndpoints = {
  rules: () => apiClient.get('/quality/rules'),

  createRule: (rule: any) => apiClient.post('/quality/rules', rule),

  updateRule: (id: string, rule: any) => apiClient.put(`/quality/rules/${id}`, rule),

  deleteRule: (id: string) => apiClient.delete(`/quality/rules/${id}`),

  runChecks: (datasetId: string, ruleIds?: string[]) =>
    apiClient.post(`/quality/datasets/${datasetId}/check`, { rule_ids: ruleIds }),

  history: (datasetId: string, params?: { page?: number; limit?: number }) =>
    apiClient.get(`/quality/datasets/${datasetId}/history`, { params }),
};

/**
 * Admin endpoints
 */
export const adminEndpoints = {
  // User management
  users: {
    list: (params?: { page?: number; limit?: number }) =>
      apiClient.get('/admin/users', { params }),

    get: (id: string) => apiClient.get(`/admin/users/${id}`),

    create: (user: any) => apiClient.post('/admin/users', user),

    update: (id: string, user: any) => apiClient.put(`/admin/users/${id}`, user),

    delete: (id: string) => apiClient.delete(`/admin/users/${id}`),

    resendSetupLink: (id: string) =>
      apiClient.post(`/admin/users/${id}/resend-setup-link`),
  },

  // Role management
  roles: {
    list: () => apiClient.get('/admin/roles'),

    get: (id: string) => apiClient.get(`/admin/roles/${id}`),

    create: (role: any) => apiClient.post('/admin/roles', role),

    update: (id: string, role: any) => apiClient.put(`/admin/roles/${id}`, role),

    delete: (id: string) => apiClient.delete(`/admin/roles/${id}`),
  },

  // System configuration
  config: {
    get: () => apiClient.get('/admin/config'),

    update: (config: any) => apiClient.put('/admin/config', config),
  },

  // System health
  health: () => apiClient.get('/admin/health'),

  // Metrics
  metrics: () => apiClient.get('/admin/metrics'),
};

/**
 * Schema endpoints
 */
export { schemaEndpoints } from './schemas';

/**
 * Monitor endpoints
 */
export { monitorEndpoints } from './monitors';

/**
 * Query endpoints
 */
export { queryEndpoints } from './query';

/**
 * Saved query endpoints
 */
export const savedQueryEndpoints = {
  list: (datasetId: string) =>
    apiClient.get(`/datasets/${datasetId}/saved-queries`),

  create: (datasetId: string, data: SavedQueryCreate) =>
    apiClient.post(`/datasets/${datasetId}/saved-queries`, data),

  remove: (datasetId: string, id: string) =>
    apiClient.delete(`/datasets/${datasetId}/saved-queries/${id}`),
};

/**
 * Export all endpoint modules
 */
export const api = {
  auth: authEndpoints,
  sources: sourceEndpoints,
  datasets: datasetEndpoints,
  ingestion: ingestionEndpoints,
  catalog: catalogEndpoints,
  quality: qualityEndpoints,
  admin: adminEndpoints,
  savedQueries: savedQueryEndpoints,
};
